import { Link, useNavigate, useParams } from "react-router";

import { useCellDetail, useCells, useMe, useRegisterPlanIntent, useRepositories, useSummary } from "../api/queries";
import { BenchmarkChart, chartSummary } from "../results/BenchmarkChart";
import type { Metric, ValueField } from "../results/BenchmarkChart";
import { ChartTabs, SECTIONS } from "../results/ChartTabs";
import { formatCost, formatCount, formatDuration, formatPercent, UNKNOWN } from "../results/format";
import { agentName, cellMatchesConfig, modelName } from "../results/present";
import type {
  Cell,
  ConfigSummary,
  Repository,
  RepositoryStats,
  Trace,
} from "../api/types.gen";
import { Icon } from "../components/Icons";

/**
 * The repository dashboard: aggregate charts over everything a workspace has
 * uploaded for one repository.
 *
 * Repository-level only, by decision. No per-challenge matrix and no trace
 * view — those come after the product is validated, and the data for both is
 * already in Postgres when they do.
 *
 * A port of `design-prototype/dashboard.js`, with one systematic difference:
 * every number here was computed by the server. The prototype's hero counters
 * (`14`/`7`/`32`/`64`), its "N of 14 setups" pill, its repository name and its
 * "August 12, 2026" were template literals over fourteen hardcoded rows, and
 * its `rowColor`, `agentName` and `modelName` switched on those rows' ids.
 * Anything still hardcoded on this screen would be a number that stays right
 * only until someone benchmarks something else.
 *
 * This screen also computes NO aggregate of its own. It renders the rollup
 * `internal/results` wrote at ingest — see `useSummary`. A leaderboard built
 * here could disagree with that one, and with `berbench report` in the
 * terminal, over the same cells and nothing would catch it.
 */
export function Dashboard() {
  const { data: me } = useMe();
  const slug = me?.workspaces[0]?.slug;

  const { data: repos, isPending: reposPending } = useRepositories(slug);

  // The selection lives in the URL, because `berbench upload` prints one:
  // /repositories/<id> has to open that repository and not merely the first.
  // Keeping it in component state as well would give the same screen two
  // sources of truth that drift on the back button.
  const { repositoryID } = useParams();

  // The first repository until the URL names another. An id that no longer
  // exists falls back rather than rendering an empty screen — a repository can
  // be deleted between someone copying a link and following it.
  const repositories = repos?.repositories ?? [];
  const selected =
    repositories.find((repo) => repo.id === repositoryID) ?? repositories[0] ?? null;

  const { data, isPending: summaryPending } = useSummary(slug, selected?.id);

  if (reposPending || (selected !== null && summaryPending)) {
    return (
      <div className="app-boot" role="status">
        Loading benchmark results…
      </div>
    );
  }

  if (selected === null) {
    return <Onboarding />;
  }

  return (
    <Report
      slug={slug}
      repository={data?.repository ?? selected}
      configs={data?.configs ?? []}
      stats={data?.stats ?? EMPTY_STATS}
      truncated={data?.truncated ?? false}
    />
  );
}

/** Repository setup, linked from the header repository selector. */
export function RepositorySetup() {
  return <Onboarding />;
}

const EMPTY_STATS: RepositoryStats = {
  challenges: 0,
  configurations: 0,
  runs: 0,
  verdicts: 0,
  errors: 0,
  experiments: [],
};

interface ReportProps {
  slug: string | undefined;
  repository: Repository;
  configs: ConfigSummary[];
  stats: RepositoryStats;
  truncated: boolean;
}

function Report({ slug, repository, configs, stats, truncated }: ReportProps) {
  if (configs.length === 0) {
    return <Onboarding />;
  }

  // The highlight cards show only the comparison, capped at six. A highlight
  // card is the one place a reader will not check the coverage key, so it is
  // the one place a non-comparable row must not appear at all.
  const top = (field: ValueField, descending: boolean) =>
    configs
      .filter((row) => row.coverage_state === "comparable" && row[field] !== undefined)
      .sort((a, b) => {
        const av = a[field] as number;
        const bv = b[field] as number;
        return descending ? bv - av : av - bv;
      })
      .slice(0, 6);

  return (
    <>
      <section className="hero">
        <div className="hero-grid">
          <div className="hero-copy">
            <div className="hero-kicker">
              <span className="repo-avatar hero-avatar">
                {(repository.owner || repository.repo_id).slice(0, 1).toUpperCase()}
              </span>
              <span>Benchmark results</span>
              <i />
              <span>Updated {formatUpdated(stats.updated_at)}</span>
            </div>
            <h1>{repositoryName(repository)}</h1>

            <div className="hero-meta">
              <Stat value={stats.challenges} label="validated challenges" />
              <Stat value={stats.experiments.length} label="experiments" />
              <Stat value={stats.configurations} label="configurations" />
              <Stat value={stats.runs} label="agent runs" />
            </div>

            <p className="coverage-note">
              <span />
              <strong>{stats.verdicts} measured outcomes</strong>
              <span className="coverage-divider">·</span>
              {stats.errors} infrastructure or adapter errors excluded
            </p>

            {truncated && (
              <p className="coverage-note" role="status">
                <span />
                These charts cover the most recent runs only. Older results are
                stored and are not on this chart.
              </p>
            )}
          </div>

          <aside className="index-note experiment-summary">
            <div className="summary-heading">
              <div>
                <span className="eyebrow">Coverage</span>
                <h2>Included experiments</h2>
              </div>
              <span className="coverage-badge">
                {stats.challenges} / {stats.challenges} valid
              </span>
            </div>

            <div className="experiment-list">
              <ExperimentList experiments={stats.experiments} />
            </div>

            <div className="summary-foot">
              <span>Averaged across every experiment</span>
              <a href="#performance">View charts <span aria-hidden="true">→</span></a>
            </div>
          </aside>
        </div>
      </section>

      <section className="highlights">
        <h2 className="section-label">Highlights</h2>
        <div className="highlight-grid">
          <HighlightCard
            title="BER Index"
            note="Pass@1 across measured challenges · Higher is better"
            rows={top("agent_index", true)}
            metric="index"
            field="agent_index"
          />
          <HighlightCard
            className="time"
            title="Time per Challenge"
            note="Average active agent runtime · Lower is better"
            rows={top("mean_agent_seconds", false)}
            metric="time"
            field="mean_agent_seconds"
          />
          <HighlightCard
            className="cost"
            title="Cost per Challenge"
            note="Average agent-reported USD · Lower is better"
            rows={top("mean_reported_cost_usd", false)}
            metric="cost"
            field="mean_reported_cost_usd"
          />
        </div>
      </section>

      <div className="analysis-body">
        <div className="analysis-content">
          {SECTIONS.map((section) => (
            <ChartTabs key={section.id} section={section} rows={configs} />
          ))}
          <TrajectoryPreview slug={slug} repositoryID={repository.id} configs={configs} />
        </div>
      </div>
    </>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function HighlightCard({
  className,
  title,
  note,
  rows,
  metric,
  field,
}: {
  className?: string;
  title: string;
  note: string;
  rows: ConfigSummary[];
  metric: Metric;
  field: ValueField;
}) {
  return (
    <article className={className ? `highlight-card ${className}` : "highlight-card"}>
      <div className="highlight-title">
        <i />
        <h2>{title}</h2>
      </div>
      <p>{note}</p>
      {rows.length > 0 ? (
        <BenchmarkChart
          rows={rows}
          metric={metric}
          field={field}
          title={`${title} highlights`}
          mini
        />
      ) : (
        // Nothing is comparable yet. Saying so beats an empty chart frame,
        // which reads as a rendering failure.
        <p className="highlight-empty">
          No configurations share enough challenges to compare yet.
        </p>
      )}
    </article>
  );
}

/**
 * The experiments this repository has been reached by — an inventory, not a
 * control.
 *
 * These rows used to be a filter, and narrowing by one was a different request
 * for a different stored rollup. That is gone: the dashboard shows one number
 * per configuration, averaged over every experiment, and this box says which
 * experiments went into it. The scoped rollups are still computed at ingest, so
 * bringing the filter back is a UI change rather than a re-upload.
 *
 * Averaging over all of them is not the same as summing the scopes, which is
 * why nothing here is derived on the client: a cell is content-addressed, so it
 * belongs to EVERY experiment that reached it, and the scopes overlap.
 */
function ExperimentList({ experiments }: { experiments: string[] }) {
  if (experiments.length === 0) {
    return <p className="experiment-empty">No experiments named on these runs.</p>;
  }

  return (
    <>
      {experiments.map((name) => (
        <div key={name} className="experiment-pill">
          <span className={`experiment-icon ${experimentIconClass(name)}`}>
            {name.slice(0, 1).toUpperCase()}
          </span>
          <span>
            <strong>{name}</strong>
            <small>{experimentDescription(name)}</small>
          </span>
        </div>
      ))}
    </>
  );
}

function experimentDescription(name: string): string {
  const normalized = name.toLowerCase();
  if (normalized.includes("context")) return "Claude Code and Codex · 8 configurations";
  if (normalized.includes("baseline")) return "Sonnet 5 · 5 repeated attempts";
  if (normalized.includes("plan") || normalized.includes("build")) {
    return "Opus 5 planner · Sonnet 5 builder";
  }
  return "Included in repository results";
}

function experimentIconClass(name: string): string {
  const normalized = name.toLowerCase();
  if (normalized.includes("baseline")) return "baseline-icon";
  if (normalized.includes("plan") || normalized.includes("build")) return "workflow-icon";
  return "";
}

/**
 * How many timeline steps are shown before the blur starts.
 *
 * The gate is real: the remaining steps ARE withheld. What changed is what is
 * behind the blur — it used to be three invented rows, and it is now the rest
 * of a run that actually happened.
 */
const TRAJECTORY_PREVIEW_STEPS = 3;

/**
 * The trajectory teaser, over a real run.
 *
 * Everything on this section used to be a literal: `Challenge #811`, "Fix
 * strftime %Z for negative offsets", "14 turns", six invented timeline rows and
 * "82% of actions contributed to the final patch". None of it was measured, and
 * the last one is the worst kind of invention — a statistic with a decimal
 * point and a progress bar, describing a computation nobody has written.
 *
 * Every string here now comes from `cells.detail`, which has carried the CLI's
 * `insight.Trace` since day one with no query reading it. The timeline rows are
 * the agent's own text, rendered verbatim and truncated by CSS; the aside is
 * the parsed clock and the agent's own closing message. Nothing on this screen
 * is written ABOUT the run by anything except the agent that made it.
 */
function TrajectoryPreview({
  slug,
  repositoryID,
  configs,
}: {
  slug: string | undefined;
  repositoryID: string;
  configs: ConfigSummary[];
}) {
  // The rank-1 configuration, which the summary has already ranked. Dense
  // ranks tie, so a tie is broken on config_key — deterministically, so the
  // same repository opens on the same run for everybody.
  const best = configs
    .filter((row) => row.rank === 1)
    .sort((a, b) => a.config_key.localeCompare(b.config_key))[0];

  // The cells, for SELECTION only. This is the second request this screen
  // makes and the larger one — the summary is fourteen rows and this is every
  // cell — and it is worth naming the cost: it buys picking a real run to show
  // rather than describing an invented one. If it ever needs to stop being
  // worth it, the fix is an endpoint that returns one cell id, not a client
  // that guesses.
  const { data: cellData } = useCells(slug, repositoryID);
  const cell = pickTrajectoryCell(cellData?.cells ?? [], best);

  const { data: detail, isPending } = useCellDetail(slug, repositoryID, cell?.id);

  return (
    <section className="analysis-section trajectory-section" id="trajectory">
      <div className="section-intro">
        <span className="eyebrow">Coming with Premium</span>
        <h2>Agent trajectory analysis</h2>
        <p>
          Move beyond the final score and inspect how the agent explored,
          reasoned, edited, recovered, and verified its work.
        </p>
      </div>
      {cell === undefined ? (
        <TrajectoryUnavailable
          reason="No run has been uploaded for this repository yet."
        />
      ) : isPending ? (
        <TrajectoryUnavailable reason="Loading this run…" />
      ) : detail?.trace == null ? (
        // A cell with no trace is a normal state, not a failure: an older CLI,
        // a `script` tool, or a stream no parser recognized. Saying so beats a
        // skeleton of placeholder rows, which is the same lie in a quieter
        // font.
        <TrajectoryUnavailable
          reason="This run was uploaded without an agent trace. Traces are recorded by current versions of the CLI — your next upload will include one."
        />
      ) : (
        <TrajectoryShell cell={detail.cell} trace={detail.trace} config={best} />
      )}
    </section>
  );
}

/**
 * Which run the teaser opens on.
 *
 * The best configuration's most convincing run: a pass if it has one, most
 * recently finished. Falling back to any cell of that configuration and then to
 * any cell at all, because a teaser that renders nothing because the winner
 * happens to have no cell is worse than one that shows a different run.
 *
 * This is selection over rows the server ranked, not a ranking. See
 * `cellMatchesConfig`.
 */
function pickTrajectoryCell(cells: Cell[], config: ConfigSummary | undefined): Cell | undefined {
  const finished = (cell: Cell) => cell.client_finished_at ?? "";
  const newestFirst = (a: Cell, b: Cell) => finished(b).localeCompare(finished(a));

  const mine =
    config === undefined ? [] : cells.filter((cell) => cellMatchesConfig(cell, config));

  const passed = mine.filter((cell) => cell.status === "passed").sort(newestFirst);
  if (passed.length > 0) return passed[0];

  const any = [...mine].sort(newestFirst);
  if (any.length > 0) return any[0];

  return [...cells].sort(newestFirst)[0];
}

/** The section with nothing to draw, saying which nothing it is. */
function TrajectoryUnavailable({ reason }: { reason: string }) {
  return (
    <div className="trajectory-shell">
      <div className="trajectory-toolbar">
        <div>
          <strong>Agent trajectory</strong>
          <span>{reason}</span>
        </div>
      </div>
    </div>
  );
}

function TrajectoryShell({
  cell,
  trace,
  config,
}: {
  cell: Cell;
  trace: Trace;
  config: ConfigSummary | undefined;
}) {
  const upgrade = useTrajectoryUpgrade();
  const shown = trace.timeline.slice(0, Math.max(TRAJECTORY_PREVIEW_STEPS + 6, 9));

  return (
    <div className="trajectory-shell">
      <div className="trajectory-toolbar">
        <div>
          <span className="status-dot" />
          <strong>Challenge #{cell.challenge_id}</strong>
          <span>
            {config === undefined
              ? cell.tool
              : `${agentName(config)} · ${modelName(config)}`}
          </span>
        </div>
        <div className="trajectory-pills">
          <span>{statusLabel(cell.status)}</span>
          <span>{turnsLabel(trace)}</span>
          <span>{formatDuration(cell.agent_seconds)}</span>
        </div>
      </div>

      <div className="trajectory-layout">
        <aside className="trajectory-summary">
          <span className="eyebrow">Run summary</span>
          <div className="trajectory-score">
            <strong>{formatCount(trace.tools.total)}</strong>
            <span>tool calls</span>
          </div>
          <dl>
            <div>
              <dt>Assistant turns</dt>
              <dd>{formatCount(trace.turns.assistant || trace.turns.reported)}</dd>
            </div>
            <div>
              <dt>Files changed</dt>
              <dd>{formatCount(trace.tools.files_changed)}</dd>
            </div>
            <div>
              <dt>Failed calls</dt>
              <dd>{formatCount(trace.tools.failed)}</dd>
            </div>
            <div>
              <dt>Denied calls</dt>
              <dd>{formatCount(trace.tools.denied)}</dd>
            </div>
            <div>
              <dt>Peak context</dt>
              {/* peak_pct is 0 when the tool never published a window — codex
                  today — and that is "not measured", not "used nothing". */}
              <dd>{trace.context.peak_pct > 0 ? formatPercent(trace.context.peak_pct / 100) : UNKNOWN}</dd>
            </div>
          </dl>
        </aside>

        <div className="trajectory-timeline">
          <div className="timeline-head">
            <strong>Trajectory</strong>
            <span>
              First {TRAJECTORY_PREVIEW_STEPS} of {formatCount(trace.timeline_total)} steps
            </span>
          </div>
          {shown.map((event, index) => (
            <div
              className={index >= TRAJECTORY_PREVIEW_STEPS ? "timeline-row locked-step" : "timeline-row"}
              key={event.seq}
            >
              <span className="timeline-index">{String(index + 1).padStart(2, "0")}</span>
              <code>{clockAt(event.at, trace.timeline[0]?.at)}</code>
              <span className="timeline-kind">{kindLabel(event.kind)}</span>
              {/* The agent's own text, verbatim. The CSS truncates it; nothing
                  here rewrites, summarizes or classifies it. */}
              <strong>{event.detail || event.name || kindLabel(event.kind)}</strong>
              <time>{event.duration_ms > 0 ? formatMillis(event.duration_ms) : ""}</time>
            </div>
          ))}
        </div>

        <aside className="trajectory-insight">
          <span className="eyebrow">Where the time went</span>
          <TimingSplit trace={trace} />
          {trace.stop.summary !== "" && (
            <>
              <h3>The agent&rsquo;s own summary</h3>
              {/* Verbatim, and truncated by the CLI rather than here. This is
                  the one piece of prose on the screen, and the agent wrote it. */}
              <p>{trace.stop.summary}</p>
            </>
          )}
        </aside>
      </div>

      <div className="trajectory-gate">
        <span className="lock-mark"><Icon name="lock" /></span>
        <span className="eyebrow">Continue with Premium</span>
        <h3>See every trajectory</h3>
        <p>
          Unlock the remaining {formatCount(Math.max(trace.timeline_total - TRAJECTORY_PREVIEW_STEPS, 0))}{" "}
          steps of this run, and trajectory analysis across every challenge.
        </p>
        <button type="button" onClick={upgrade}>Upgrade to Premium</button>
        <small>Includes task-level charts and advanced efficiency metrics</small>
      </div>
    </div>
  );
}

/**
 * The clock split, from `timing.buckets`.
 *
 * Three bars rather than one, because the buckets are three quantities and a
 * single bar would have to pick one to be "the" number. Subagent time is left
 * out deliberately: it OVERLAPS the other three rather than partitioning
 * alongside them, so a fourth bar on the same scale would sum to more than the
 * run took.
 *
 * `unaccounted` is drawn rather than hidden. A large unaccounted share means
 * the events do not explain the wall clock, which is a real thing to know about
 * a run and exactly the sort of number a prettier chart would quietly drop.
 */
function TimingSplit({ trace }: { trace: Trace }) {
  const { tool_ms, model_ms, unaccounted_ms } = trace.timing;
  const total = tool_ms + model_ms + unaccounted_ms;
  if (total <= 0) {
    return <p>This run&rsquo;s stream carried no timestamps, so its clock cannot be split.</p>;
  }

  const parts: Array<[string, number]> = [
    ["Model", model_ms],
    ["Tools", tool_ms],
    ["Unaccounted", unaccounted_ms],
  ];

  return (
    <>
      {parts.map(([label, ms]) => (
        <div key={label}>
          <div className="insight-bar">
            <span style={{ width: `${(ms / total) * 100}%` }} />
          </div>
          <small>
            {label} · {formatPercent(ms / total)} · {formatMillis(ms)}
          </small>
        </div>
      ))}
    </>
  );
}

/**
 * The trajectory gate's Upgrade button.
 *
 * Its own source, like every other one: a click from here says the trajectory
 * is what someone wants, which is a different answer from a click on a locked
 * cache chart.
 */
function useTrajectoryUpgrade() {
  const { data: me } = useMe();
  const register = useRegisterPlanIntent(me?.workspaces[0]?.slug);
  const navigate = useNavigate();

  return () => {
    register.mutate({ plan: "pro", source: "trajectory_gate" });
    navigate("/plans");
  };
}

/** The CLI's status, in the words the pill has room for. */
function statusLabel(status: string): string {
  if (status === "passed") return "Passed";
  if (status === "failed") return "Failed";
  // Everything else is the harness rather than the agent — adapter_error,
  // timeout, infrastructure_error — and reads better unshouted than mapped to
  // a word this file would have to keep in step with the CLI's vocabulary.
  return status.replace(/_/g, " ");
}

/**
 * The turn count, preferring the agent's own.
 *
 * `reported` is what the tool says; `assistant` counts stream events and is
 * usually larger. Neither is wrong, so the agent's own number is shown when it
 * published one and the counted one is labelled as counted when it did not.
 */
function turnsLabel(trace: Trace): string {
  if (trace.turns.reported > 0) return `${trace.turns.reported} turns`;
  if (trace.turns.assistant > 0) return `${trace.turns.assistant} messages`;
  return UNKNOWN;
}

/** The timeline pill: the CLI's event kind, made readable. */
function kindLabel(kind: string): string {
  switch (kind) {
    case "tool_use":
      return "Call";
    case "tool_result":
      return "Result";
    case "subagent_started":
      return "Subagent";
    case "permission_denial":
      return "Denied";
    case "text":
      return "Reason";
    default:
      return kind.replace(/_/g, " ");
  }
}

/** An event's offset from the run's first event, as mm:ss. */
function clockAt(at: string, first: string | undefined): string {
  if (at === "" || first === undefined || first === "") return "";
  const started = Date.parse(first);
  const now = Date.parse(at);
  if (Number.isNaN(started) || Number.isNaN(now)) return "";
  const seconds = Math.max(0, Math.round((now - started) / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

/** A duration the trace reports in milliseconds. */
function formatMillis(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return formatDuration(ms / 1000);
}

/**
 * The empty state, which is the onboarding surface.
 *
 * A workspace with no results is the most common state a new account is in, and
 * the single most useful thing to put on that screen is the exact sequence of
 * commands that fills it. The prototype's empty state said "No aggregate
 * benchmark yet" and offered a button back to the demo repository, which is not
 * an option a real account has.
 */
// It used to take a repository and an experiment as well, for the case of a
// repository whose cells the experiment filter had excluded — a different
// problem from a workspace with nothing in it, and one where telling someone to
// run `berbench init` would have been noise. With the filter gone an empty
// rollup means the repository is genuinely empty, so there is one empty state
// again rather than two.
function Onboarding() {
  return (
    <section className="dashboard-onboarding">
      <div className="onboarding-hero-copy">
        <div className="onboarding-kicker">
          <span className="empty-mark">01</span>
          <span>Your first benchmark</span>
        </div>
        <h1>See how coding agents perform on your codebase.</h1>
        <p>
          Turn merged work from your repository into repeatable benchmarks,
          compare agent setups, and bring the results back here.
        </p>
        <div className="onboarding-hero-actions">
          <Link className="onboarding-primary" to="/cli">
            <Icon name="terminal" /> Connect the CLI <Icon name="arrow" />
          </Link>
          <a className="onboarding-secondary" href="https://benchdocs.ber.run" target="_blank" rel="noreferrer">
            Read the setup guide
          </a>
        </div>
        <small className="onboarding-local-note">
          <Icon name="lock" /> Your repository stays local. Only benchmark results are uploaded.
        </small>
      </div>

      <aside className="onboarding-terminal-card" aria-label="BERBench setup commands">
        <div className="terminal-card-head">
          <span><i /> <i /> <i /></span>
          <strong>Quick start</strong>
          <em>~ 5 minutes</em>
        </div>
        <ol className="onboarding-steps">
          <li>
            <code>berbench init</code>
            <span>Prepare your repository</span>
          </li>
          <li>
            <code>berbench challenge create</code>
            <span>Create a challenge from merged work</span>
          </li>
          <li>
            <code>berbench run</code>
            <span>Run your agent comparison</span>
          </li>
          <li>
            <code>berbench upload</code>
            <span>Publish results to this dashboard</span>
          </li>
        </ol>
        <div className="terminal-card-foot">
          <span className="live-dot" />
          This dashboard updates after your first upload
        </div>
      </aside>

      <div className="onboarding-outcomes">
        <article>
          <span>01</span>
          <div><strong>Use real work</strong><p>Benchmark against merged changes from your own repository.</p></div>
        </article>
        <article>
          <span>02</span>
          <div><strong>Compare configurations</strong><p>Measure models, tools, cost, speed, and successful outcomes.</p></div>
        </article>
        <article>
          <span>03</span>
          <div><strong>Make a decision</strong><p>See which agent setup performs best for your team.</p></div>
        </article>
      </div>
    </section>
  );
}

/** `owner / name`, or the CLI's repo_id when it could not tell. */
function repositoryName(repo: Repository): string {
  if (repo.owner !== "" && repo.name !== "") return `${repo.owner} / ${repo.name}`;
  return repo.name || repo.repo_id;
}

/**
 * When the most recent measured run finished.
 *
 * Null when no cell carried a client clock, which is a real state and not an
 * error — the timestamp is the client's, recorded and not trusted.
 */
function formatUpdated(iso: string | undefined): string {
  if (iso === undefined) return "recently";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "recently";
  return at.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Re-exported so the summary a screen reader receives can be asserted on
// directly in a test, without mounting a canvas jsdom cannot draw to.
export { chartSummary, formatCost, formatDuration };
